import type { TicketDetailData, UserInfo } from '@/entities/ticket';
import { formatWorkMinutes } from '@/entities/ticket';
import { isEmptyValue, isLayoutField, type FormField } from '@/shared/api/form-schema';
import { cn } from '@/shared/lib/cn';
import styles from './TicketDetail.module.css';

export function slaStateCaption(sla: NonNullable<TicketDetailData['sla']>): string {
  if (sla.paused) return 'на паузе';
  if (sla.level === 'breached') return sla.kind === 'reaction' ? 'ответ просрочен' : 'решение просрочено';
  return sla.kind === 'reaction' ? 'до ответа' : 'до решения';
}

export function slaReactionText(reaction: TicketDetailData['slaReaction']): string | null {
  if (!reaction) return null;
  const spent = reaction.spentMinutes != null ? ` за ${formatWorkMinutes(reaction.spentMinutes)}` : '';
  return `Первый ответ${spent}${reaction.breached ? ` — позже нормы ${formatWorkMinutes(reaction.targetMinutes)}` : ' — в срок'}`;
}

export const cnChip = (active?: boolean) => cn(styles.chip, active && styles.chipActive);

export function getUserDisplayName(
  user: UserInfo | null | undefined,
  fallbackId?: string | null,
): string {
  if (user?.displayName) return user.displayName;
  if (fallbackId) return fallbackId;
  return 'Неизвестный пользователь';
}

export interface FormAnswerGroup {
  title: string | null;
  fields: FormField[];
}

export function groupAnswers(fields: FormField[], data: Record<string, unknown>): FormAnswerGroup[] {
  const groups: FormAnswerGroup[] = [{ title: null, fields: [] }];
  for (const field of fields) {
    if (field.type === 'section') groups.push({ title: field.label, fields: [] });
    else if (isLayoutField(field)) continue;
    else if (field.type === 'checkbox' && field.submitOnly) continue;
    else if (!isEmptyValue(data[field.key])) groups[groups.length - 1].fields.push(field);
  }
  return groups.filter((g) => g.fields.length > 0);
}

function projectionHead(fields: FormField[], data: Record<string, unknown>): string | null {
  const first = groupAnswers(fields, data)[0];
  if (!first) return null;
  return first.title ? `— ${first.title} —` : `${first.fields[0].label}: `;
}

export function descriptionWithoutForm(ticket: TicketDetailData): string {
  const text = ticket.description ?? '';
  const fields = ticket.formSchema?.fields;
  const data = ticket.formData;
  if (!fields || !data) return text;
  const head = projectionHead(fields, data);
  if (!head) return text;
  if (text.startsWith(head)) return '';
  const at = text.lastIndexOf(`\n\n${head}`);
  return at >= 0 ? text.slice(0, at) : text;
}

export function hasFormAnswers(ticket: TicketDetailData): boolean {
  const fields = ticket.formSchema?.fields;
  return !!fields && !!ticket.formData && groupAnswers(fields, ticket.formData).length > 0;
}
